<template>
  <Modal
    title="Complete Warranty Program Purchase & Setup"
    class-name="completeWarrantyProgramPurchaseModal"
    submit-btn-text="Save & Complete"
    delete-btn-text="Ignore"
    :disable-submit="isSaveAndCompleteDisabled"
    :no-footer="isDone"
    @submit="handleSaveAndComplete"
    @delete="handleIgnore"
    @hide-modal="$emit('hide-modal')"
  >
    <div v-if="loading">
      <Spinner v-if="loading" message="Loading Warranty Program details..." />
    </div>
    <div v-else>
      <div v-if="isDone">
        <span>Work item was previously {{ status }}</span>
        <span v-if="owner"
          >by <strong>{{ owner.full_name }}</strong></span
        >
        <span>on {{ dateCompletedDisplay }}</span>
      </div>
      <div v-else class="sections">
        <!-- Customer Details -->
        <div class="customerDetails">
          <div class="customerDetails__name">
            <a :href="getCustomerLink(customer)" target="_blank">
              <strong>{{ customer.full_name }}</strong>
            </a>
          </div>
          <div class="customerDetails__address">
            {{ fullAddress(invoice.service_address) }}
          </div>
        </div>

        <!-- Sold Warranty Program -->
        <div class="soldWarrantyProgram">
          <div class="mb-2">
            <h4>Sold Warranty Program</h4>
          </div>
          <EquipmentWarrantyForm
            ref="equipmentWarrantyForm"
            v-model="formData.soldWarrantyProgram"
            :warranty-program="{
              name: soldWarranty.warranty_program_name,
              provider_name: soldWarranty.warranty_program_provider_name,
              labor_rate_cents: soldWarranty.labor_rate_cents,
              term_years: soldWarranty.term_years,
              cost_cents: soldWarranty.cost_cents,
              sku_number: soldWarranty.sku_number,
              effectiveAfterDays
            }"
          />
        </div>

        <!-- Invoice Details -->
        <div class="invoiceDetails">
          <div class="tw-flex tw-items-center mb-1">
            <h4 class="mb-3">
              Invoice #{{ invoice.invoice_id }} Equipment - Qty #{{
                workItem.unit_number
              }}
              of {{ lineItem.quantity }}
            </h4>
            <a class="ml-auto" :href="getInvoiceLink(invoice)"
              >Change mapping on invoice</a
            >
          </div>
          <div v-if="lineItem.equipment_links.length === 0">
            <span>No linked equipment found for this invoice.</span>
          </div>
          <div class="section-spacing-20">
            <div
              v-for="equipmentLink in equipmentLinksWithNoEquipmentFirst"
              :key="equipmentLink.id"
            >
              <div>
                <span
                  >{{ equipmentLink.optional ? "Optional" : "Requested" }}
                  equipment information for
                  {{ equipmentLink.equipment_type.name }}:</span
                >
                <span v-if="!equipmentLink.equipment"> --</span>
              </div>
              <EquipmentCard
                v-if="equipmentLink.equipment"
                v-bind="equipmentLink.equipment"
                :equipment-type-name="equipmentLink.equipment_type.name"
                :service-history="
                  equipmentIdToServiceHistory[equipmentLink.equipment.id]
                "
                default-collapsed
                :components="
                  activeComponents(equipmentLink.equipment.components)
                "
                class="mb-2"
                :data-cy-id="`equipment-card-${equipmentLink.equipment.id}`"
              >
                <template #componentsTable>
                  <EquipmentComponentsTable
                    :components="
                      activeComponents(equipmentLink.equipment.components)
                    "
                  />
                </template>
                <template #serviceHistoryTable>
                  <EquipmentServiceHistoryTable
                    :service-history="
                      equipmentIdToServiceHistory[equipmentLink.equipment.id]
                    "
                  />
                </template>
              </EquipmentCard>
            </div>
          </div>
        </div>

        <!-- Select Program Equipment -->
        <div class="selectProgramEquipment">
          <div class="tw-flex tw-items-center mb-2">
            <h4>Select Warranty Program Equipment</h4>
            <a class="ml-auto" :href="getInvoiceLink(invoice)"
              >Change mapping on invoice</a
            >
          </div>
          <Select
            v-model="formData.selectedWarrantyProgramEquipmentId"
            :disabled="equipmentLinksWithEquipment.length === 0"
            :placeholder="selectWarrantyProgramPlaceholder"
            name="*Warranty Program Equipment"
            full-width
            :options="equipmentOptions"
            :validation-errors="v$.$errors"
          />
          <WarningMessage
            v-if="showWarningMessageAboutSelectedEquipment"
            class="mt-2"
            >Adding the sold warranty program to the selected equipment will
            remove the existing warranty program</WarningMessage
          >
        </div>

        <!-- Summary of Changes -->
        <div v-if="changeSummary.length">
          <div class="changeSummary">
            <strong>Summary of Changes:</strong>
            <ul>
              <li v-for="change in changeSummary" :key="change">
                <span>{{ change }}</span>
              </li>
            </ul>
            <div v-if="note"><strong>Note:</strong> {{ note }}</div>
          </div>
        </div>
      </div>
    </div>
  </Modal>
</template>

<script>
import sortBy from "lodash/sortBy";
import moment from "moment";
import WarningMessage from "serviceshift-ui/components/General/WarningMessage.vue";
import { mapActions } from "vuex";

import Modal from "@/components/Modal.vue";
import Spinner from "@/components/Spinner.vue";
import EquipmentComponentsTable from "@/ComponentTypes/EquipmentComponentsTable.vue";
import EquipmentServiceHistoryTable from "@/Equipment/EquipmentServiceHistoryTable.vue";
import EquipmentWarrantyForm from "@/Equipment/EquipmentWarrantyForm.vue";
import { dollars, fullAddress } from "@/mixins";
import { userShowPath } from "@/mixins/path_helpers.js";
import useVuelidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";

export default {
  components: {
    Spinner,
    EquipmentComponentsTable,
    EquipmentServiceHistoryTable,
    EquipmentWarrantyForm,
    Modal,
    WarningMessage
  },
  props: {
    invoice: {
      type: Object,
      default: () => null
    },
    soldWarranty: {
      type: Object,
      default: () => null
    },
    workItem: {
      type: Object,
      default: () => null
    },
    lineItem: {
      type: Object,
      default: () => null
    },
    effectiveAfterDays: {
      type: Number,
      default: () => 0
    }
  },
  emits: ["hide-modal", "ignore", "save-and-complete"],
  setup() {
    return {
      v$: useVuelidate()
    };
  },
  data() {
    return {
      equipmentIdToServiceHistory: {},
      formData: {
        selectedWarrantyProgramEquipmentId: null,
        soldWarrantyProgram: {
          purchase_date: this.soldWarranty.purchase_date,
          effective_date: this.soldWarranty.effective_date,
          expiration_date: this.soldWarranty.expiration_date
        }
      },
      effectiveAfterDays: this.effectiveAfterDays,
      loading: true,
      saving: false
    };
  },
  computed: {
    isSaveAndCompleteDisabled() {
      if (!this.workItem.links) return true;
      return !this.workItem.links.warranty_todo_complete;
    },
    selectWarrantyProgramPlaceholder() {
      if (this.equipmentLinksWithEquipment.length === 0)
        return "No linked equipment to select from - Please change mapping on invoice";
      return "Select...";
    },
    equipmentLinksWithNoEquipmentFirst() {
      return sortBy(this.lineItem.equipment_links || [], (equipmentLink) => {
        return !equipmentLink.equipment;
      }).reverse();
    },
    equipmentLinksWithEquipment() {
      return (this.lineItem.equipment_links || []).filter((equipmentLink) => {
        return !!equipmentLink.equipment;
      });
    },
    status() {
      // Can be "todo" | "completed" | "archived"
      return this.workItem.status;
    },
    owner() {
      return this.workItem.owner;
    },
    customer() {
      if (!this.invoice) return undefined;
      return this.invoice.customer;
    },
    changeSummary() {
      if (!this.formData.selectedWarrantyProgramEquipmentId) return [];
      const {
        sku_number,
        warranty_program_provider_name,
        cost_cents,
        term_years
      } = this.soldWarranty;
      const changes = [];
      if (this.soldWarranty.integration_type === "jb_warranties") {
        changes.push(
          `Warranty Program will be sent to ${warranty_program_provider_name}: ${term_years}-Year Extended Labor Warranty ${sku_number}`,
          `${warranty_program_provider_name} will charge the payment method on file for the purchase in the amount of ${dollars(
            cost_cents
          )}`
        );
      } else {
        changes.push(
          `Client will need to purchase warranty program from ${warranty_program_provider_name} directly`,
          `${warranty_program_provider_name} will charge the client for the warranty program directly`
        );
      }
      changes.push(
        `Warranty Program will be added to the equipment listed above and the existing warranty program will be removed`,
        `Work Item will be completed and removed from the queue`
      );
      return changes;
    },
    note() {
      if (!this.formData.selectedWarrantyProgramEquipmentId) return "";
      if (this.soldWarranty.integration_type === "jb_warranties")
        return "A JB Warranties representative will reach out to you if there are any issues with payment collection";
      return "";
    },
    selectedWarrantyProgramEquipmentDetails() {
      if (this.selectedWarrantyProgramEquipmentId === null) return undefined;
      return this.equipmentLinksWithEquipment.find(
        (equipmentLink) =>
          equipmentLink.equipment.id ===
          this.formData.selectedWarrantyProgramEquipmentId
      );
    },
    showWarningMessageAboutSelectedEquipment() {
      if (!this.selectedWarrantyProgramEquipmentDetails) return false;
      return Boolean(this.selectedWarrantyProgramEquipmentDetails.warranty);
    },
    dateCompletedDisplay() {
      const dateToUse =
        this.status === "archived"
          ? this.workItem.archived_at
          : this.workItem.completed_on;
      return `${moment(dateToUse).format("MMMM DD, YYYY h:mm A")} (${
        window.currentTenant.timezone_abbreviation
      })`;
    },
    isDone() {
      return this.status === "completed" || this.status === "archived";
    },
    equipmentOptions() {
      return sortBy(this.equipmentLinksWithEquipment, (equipmentLink) =>
        equipmentLink.equipment.name.toLowerCase()
      ).map((equipmentLink) => ({
        value: equipmentLink.equipment.id,
        label: equipmentLink.equipment.name
      }));
    }
  },
  async mounted() {
    this.loading = true;

    // Auto select the first equipment from the list
    if (this.equipmentLinksWithEquipment.length === 1) {
      this.formData.selectedWarrantyProgramEquipmentId =
        this.equipmentLinksWithEquipment[0].equipment.id;
    }
    try {
      await this.fetchEquipmentServiceHistory();
    } catch (e) {
      // Do nothing
    }
    this.loading = false;
  },
  validations() {
    return {
      formData: {
        selectedWarrantyProgramEquipmentId: { required, $autoDirty: true }
      }
    };
  },
  methods: {
    ...mapActions([
      "fetchServiceHistoryByEquipmentId",
      "archiveWarrantyTodo",
      "completeWarrantyTodo"
    ]),
    async fetchEquipmentServiceHistory() {
      await Promise.all(
        this.equipmentLinksWithEquipment.map(async (equipmentLink) => {
          try {
            const results = await this.fetchServiceHistoryByEquipmentId(
              equipmentLink.equipment.links.service_history
            );
            this.equipmentIdToServiceHistory[equipmentLink.equipment.id] =
              results.service_history;
          } catch (e) {
            this.equipmentIdToServiceHistory[equipmentLink.equipment.id] = [];
          }
        })
      );
    },
    fullAddress,
    dollars,
    activeComponents(components) {
      return (components || []).filter((component) => !component.archived);
    },
    getInvoiceLink(invoice) {
      return `${invoice.links.invoice_url}`;
    },
    getCustomerLink(customer) {
      return userShowPath(customer.customer_id);
    },
    async handleIgnore() {
      await this.archiveWarrantyTodo(this.workItem.links.warranty_todo_archive);
      this.$emit("hide-modal");
      this.$emit("ignore");
    },
    async handleSaveAndComplete() {
      if (!(await this.$refs.equipmentWarrantyForm.v$.$validate())) return;
      if (!(await this.v$.$validate())) return;
      await this.completeWarrantyTodo({
        link: this.workItem.links.warranty_todo_complete,
        warranty: {
          ...this.formData.soldWarrantyProgram,
          warranty_program_id: this.soldWarranty.warranty_program_id,
          equipment_id: this.formData.selectedWarrantyProgramEquipmentId
        }
      });
      window.notify.success("Warranty To-Do has been updated successfully!");
      this.$emit("hide-modal");
      this.$emit("save-and-complete");
    }
  }
};
</script>

<style lang="scss" scoped>
h4 {
  margin-bottom: 0 !important;
}
.sections {
  > * {
    padding-bottom: 15px;
    margin-bottom: 15px;
  }
}
ul {
  padding-left: 15px;
}
.warranty {
  display: flex;
  justify-content: left;
  &__title {
    font-weight: bold;
  }
  > * {
    margin-right: 30px;
    &:last-child {
      margin-right: 0;
    }
  }
}
.changeSummary {
  background: var(--light-gray);
  border: 1px solid var(--gray);
  border-radius: 5px;
  padding: 10px;
}
</style>
